import type {Command, GlobalAttributes} from '@tiptap/core';
import {Extension} from '@tiptap/core';
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {dekodTallMedBenevnelseMedNullSomFallback, erIkkeTomCellSelection} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {erTall} from "felles-ng2/støttefunksjoner/tall";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {nil} from "felles-ng2/typer/nullish";

/** Tiptap utvidelse som legger til attributtet 'marg' for bruk på tabell-celler (tableCell og tableHead) */
export const TabellcelleMarg = Extension.create({

    name: 'TabellcelleMarg',

    /** Legger til marg-attributtet på både 'tableCell' og 'tableHeader', bruker derfor addGlobalAttributes */
    addGlobalAttributes(): GlobalAttributes {
        return [
            {
                types: tillatteNodetyper(),
                attributes: {
                    marg: {
                        default: 5,
                        renderHTML: attributter => tabellcelleMargSomHtmlStyleAttributt(attributter),
                        parseHTML: element => htmlBorderStyleSomMargAttributt(element),
                        keepOnSplit: true,
                    }
                }
            }
        ];
    },

    addCommands(): TabellcelleMargKommandoer<Command> {
        return {
            setCelleMarg: (marg) => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {marg: marg}), props.chain())
                  .run();
            },

            fjernCelleMarg: () => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {marg: null}), props.chain())
                  .run();
            }
        };
    }
});

function tillatteNodetyper() {
    return [NodeType.TableCell, NodeType.TableHeader];
}

/** Style-attributter som administreres av {@link TabellcelleMarg} */
export const styleAttributterForMarg = ['padding', 'padding-left', 'padding-top', 'padding-right', 'padding-bottom']

/** Serialiserer 'marg' attributtet til html css style attributter på formen {style: <css-padding-properties som streng>} */
export function tabellcelleMargSomHtmlStyleAttributt(attributter: Record<string, any>): Record<string, any> {
    const cssFragment = somCssStyle(attributter.marg);
    return erIkkeNil(cssFragment) ? {style: cssFragment} : {};
}

function somCssStyle(marg: number | nil) {
    if (!erTall(marg)) return null;
    const margVerdi = margSomCssVerdi(marg);
    return styleAttributterForMarg
      .filter(attributt => attributt != 'padding') //vi fjerner padding-attributtet når vi genererer css-styling
      .map(attributt => `${attributt}:${margVerdi}`).join(';')
}

/** Returnere angitt tall som '<tall>px' */
function margSomCssVerdi(margstørrelse: number): string {
    return `${margstørrelse}px`;
}

/** Deserialiserer css border style properties satt på angitt element til marg representasjon */
function htmlBorderStyleSomMargAttributt(element: HTMLElement): number | null {
    return dekodTallMedBenevnelseMedNullSomFallback(element.style.padding, 'px');
}

// typedefinisjoner

interface TabellcelleMargKommandoer<ReturnType> {
    /** Setter marg på valgte celler.
     * Merk at eventuelle øvrige padding attributter satt via 'style' attributtet på {@link TabellCelle} vil bli fjernet
     * @param marg Margverdi i px. */
    setCelleMarg: (marg: number) => ReturnType;

    /** Fjerner border/outline stiling fra valgte celler */
    fjernCelleMarg: () => ReturnType;
}

//for at kommandoene skal dukke opp i editor ts typen
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        celleMarg: TabellcelleMargKommandoer<ReturnType>;
    }
}
